import dayjs, { type ConfigType, type Dayjs, type ManipulateType, type UnitType } from 'dayjs'
import 'dayjs/locale/nl.js'

dayjs.locale('nl')

export const formatDateShort = (date: ConfigType): string => dayjs(date).format('D MMM YYYY')
export const formatDateShortWithTime = (date: ConfigType): string => dayjs(date).format('D MMM YYYY - H:mm')
export const formatDate = (date: ConfigType): string => dayjs(date).format('D MMMM YYYY')
export const formatDay = (date: ConfigType): string => dayjs(date).format('D')
export const formatDayDouble = (date: ConfigType): string => dayjs(date).format('DD')
export const formatDayShort = (date: ConfigType): string => dayjs(date).format('dd')
export const formatDayFull = (date: ConfigType): string => dayjs(date).format('dddd')
export const formatTime = (date: ConfigType): string => dayjs(date).format('HH:mm')
export const formatDayMonth = (date: ConfigType): string => dayjs(date).format('dddd D MMMM')
export const formatMonthYear = (date: ConfigType): string => dayjs(date).format('MMMM YYYY')
export const formatMonth = (date: ConfigType): string => dayjs(date).format('MMM')
export const formatDayDoubleMonth = (date: ConfigType): string => dayjs(date).format('DD MMM')
export const formatYearMonthDay = (date: ConfigType): string => dayjs(date).format('YYYY-MM-DD')
export const formatDayMonthYear = (date: ConfigType): string => dayjs(date).format('DD-MM-YYYY')
export const formatDayMonthYearShort = (date: ConfigType): string => dayjs(date).format('DD-MM-YY')
export const formatMoment = (date: ConfigType): string => dayjs(date).format('dddd D MMMM HH:mm')
export const formatMomentWithYear = (date: ConfigType): string => dayjs(date).format('dddd D MMMM YYYY HH:mm')
export const formatShortDateWithTime = (date: ConfigType): string => dayjs(date).format('DD-MM-YYYY HH:mm')
export const formatShortDateWithTimeSeconds = (date: ConfigType): string =>
  dayjs(date).format('DD-MM-YYYY HH:mm:ss.SSS')
export const formatFullMonthWithDate = (date: ConfigType, locale = 'nl'): string =>
  dayjs(date, { locale }).format('D MMMM YYYY')

export const formatDateWithTime = (date: ConfigType): string => dayjs(date).format('YYYY-MM-DDTHH:mm:ss')

export const addHours = (datetime: ConfigType, hours: number): Dayjs => dayjs(datetime).add(hours, 'hour')
export const addDays = (date: ConfigType, days: number): Dayjs => dayjs(date).add(days, 'day')

export const isPast = (date: ConfigType): boolean => dayjs(date).isBefore(Date.now())
export const isToday = (date: ConfigType): boolean => dayjs(date).isSame(Date.now(), 'day')
export const isSameDay = (d1: ConfigType, d2: ConfigType): boolean => dayjs(d1).isSame(d2, 'day')
export const isAfter = (d1: ConfigType, d2: ConfigType): boolean => dayjs(d1).isAfter(d2)

export const isWeekend = (date: ConfigType): boolean => {
  const dayOfTheWeek = dayjs(date).day()
  return dayOfTheWeek === 6 || dayOfTheWeek === 0 // Is it a saturday or sunday
}

export const determineStartingDay = (date?: Date): string => {
  const today = (date && dayjs(date)) || dayjs()
  const dayOfTheWeek = today.day()
  const format = 'YYYY-MM-DD'

  if (dayOfTheWeek === 6) {
    return today.add(2, 'day').format(format)
  }
  if (dayOfTheWeek === 0) {
    return today.add(1, 'day').format(format)
  }

  return today.add(dayOfTheWeek * -1 + 1, 'day').format(format)
}

export const getFirstDateTimeInArray = (dates: string[]): Date | undefined => {
  if (dates.length === 0) {
    return undefined
  }

  return dates.reduce((acc, curr) => {
    const currDate = dayjs(curr).toDate()
    return currDate < acc ? currDate : acc
  }, dayjs(dates[0]).toDate())
}

export const getWorkdaysFromTodayArray = (startCalendarDate?: Date, numberOfDays = 21): Date[] => {
  const startDate = startCalendarDate || determineStartingDay()
  const daysArr: Date[] = []
  const startDay = dayjs(startDate)

  // eslint-disable-next-line no-plusplus
  for (let nmb = 0; nmb < numberOfDays; nmb++) {
    const addDay = startDay.add(nmb, 'day')
    daysArr.push(addDay.toDate())
  }

  return daysArr
}

export type RoundUnit = Extract<ManipulateType, UnitType>

export const roundDate = (date: Dayjs, amount: number, unit: RoundUnit): Dayjs => {
  const mod = date.get(unit) % amount
  return mod < amount / 2 ? date.subtract(mod, unit).startOf(unit) : date.add(amount - mod, unit).startOf(unit)
}

export const ceilDate = (date: Dayjs, amount: number, unit: RoundUnit): Dayjs => {
  const mod = date.get(unit) % amount
  return mod !== 0 ? date.add(amount - mod, unit).startOf(unit) : date
}
